import {Alert} from '@mantine/core'
import {AdvancedMarker, Map, MapMouseEvent, useMap} from '@vis.gl/react-google-maps'
import {useEffect} from 'react'

import type {GoogleMapsSource} from './google-maps/google-maps-metrics'
import {MapErrorBoundary} from './google-maps/map-error-boundary'
import {MapLoadObserver, useMapLoadTracking} from './google-maps/map-load-tracking'

export const CREATE_JOB_MAP_ID = 'create-job-location-map'

/** Houston, TX — default view when no pin is placed yet. */
export const DEFAULT_MAP_CENTER = {lat: 29.7604, lng: -95.3698}
export const DEFAULT_MAP_ZOOM = 8
export const LOCATION_MAP_ZOOM = 15

type LocationMapPickerProps = {
    source: GoogleMapsSource
    latitude: number | null
    longitude: number | null
    onPositionChange: (position: {latitude: number; longitude: number}) => void
    height?: number | string
    onError?: () => void
    /** Hides the map while keeping its load attempt open, so a provider fallback can finish it. */
    hidden?: boolean
}

function MapCenterUpdater({latitude, longitude}: {latitude: number; longitude: number}) {
    const map = useMap()

    useEffect(() => {
        if (!map) {
            return
        }
        map.panTo({lat: latitude, lng: longitude})
    }, [map, latitude, longitude])

    return null
}

export function LocationMapPicker({
    source,
    latitude,
    longitude,
    onPositionChange,
    height = 240,
    onError,
    hidden = false,
}: LocationMapPickerProps) {
    const tracking = useMapLoadTracking(source)
    const hasPosition = latitude != null && longitude != null
    const center = hasPosition ? {lat: latitude, lng: longitude} : DEFAULT_MAP_CENTER
    const zoom = hasPosition ? LOCATION_MAP_ZOOM : DEFAULT_MAP_ZOOM

    function handleMapClick(event: MapMouseEvent) {
        const latLng = event.detail.latLng
        if (!latLng) {
            return
        }
        onPositionChange({latitude: latLng.lat, longitude: latLng.lng})
    }

    function handleMarkerDrag(event: google.maps.MapMouseEvent) {
        const latLng = event.latLng
        if (!latLng) {
            return
        }
        onPositionChange({latitude: latLng.lat(), longitude: latLng.lng()})
    }

    if (hidden) {
        return null
    }

    return (
        <MapErrorBoundary
            fallback={
                <Alert color="orange">
                    Map unavailable. Enter latitude and longitude to continue.
                </Alert>
            }
            onError={() => {
                tracking.markRuntimeError()
                onError?.()
            }}
        >
            <Map
                reuseMaps
                defaultCenter={center}
                defaultZoom={zoom}
                gestureHandling="greedy"
                mapId={CREATE_JOB_MAP_ID}
                mapTypeId="hybrid"
                mapTypeControl={false}
                zoomControl
                // Reused maps retain options, so both map components must set every option either sets.
                fullscreenControl
                streetViewControl
                cameraControl
                keyboardShortcuts
                clickableIcons
                onTilesLoaded={tracking.markReady}
                style={{width: '100%', height, minHeight: 240, borderRadius: 8}}
                onClick={handleMapClick}
            >
                <MapLoadObserver tracking={tracking} />
                {hasPosition && (
                    <>
                        <MapCenterUpdater latitude={latitude} longitude={longitude} />
                        <AdvancedMarker
                            draggable
                            position={{lat: latitude, lng: longitude}}
                            onDragEnd={handleMarkerDrag}
                        />
                    </>
                )}
            </Map>
        </MapErrorBoundary>
    )
}
